import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Droppable, Draggable } from "react-beautiful-dnd";
import SupportTicketItem from "./SupportTicketItem";

const Container = styled.div`
  margin: 8px;
  display: flex;
  flex-direction: column;
  max-height: 65rem;
  width: 100%;
  padding: 0.5rem;
  flex: 1 0 21%;
  max-width: 335px;
  height: 100%;
  padding-top: 0;
  margin-top: 0;
  .btn-success {
    width: 100%;
  }
`;

const getBackgroundColor = (isDraggingOver, isDraggingFrom) => {
  if (isDraggingOver) {
    return "#e5e6e6";
  }
  if (isDraggingFrom) {
    return "#e5e6e6";
  }
  return "#F5F6FA";
};

const Wrapper = styled.div`
  background: ${(props) =>
    getBackgroundColor(props.isDraggingOver, props.isDraggingFrom)};
  display: flex;
  flex-direction: column;
  opacity: ${({ isDropDisabled }) => (isDropDisabled ? 0.5 : "inherit")};
  border: 8px;
  transition:
    background-color 0.2s ease,
    opacity 0.1s ease;
  user-select: none;
  width: 100%;
  max-height: 60rem;
  padding-bottom: 1rem;
  padding-top: 1rem;
  border-radius: 3px;
`;

const scrollContainerHeight = 250;

const DropZone = styled.div`
  min-height: ${scrollContainerHeight}px;
  padding-bottom: 8px;
  padding-right: 1rem;
  height: 100%;
`;

const ScrollContainer = styled.div`
  overflow-x: hidden;
  height: 100%;
`;

const ItemContainer = styled.div`
  height: fit-content;
  margin-bottom: 1em;
  border: solid 0.5px #d7d7d7;
  border-radius: 5px;
`;

// /////////////////////////////
// INNER ITEM LIST COMPONENT //
// ///////////////////////////
const InnerItemList = ({ items, name, auth, onTicketClick }) =>
  items.map((item, index) => (
    <ItemContainer key={item._id}>
      <Draggable
        key={item._id}
        id={item._id}
        draggableId={item._id}
        index={index}
        shouldRespectForceTouch={false}
      >
        {(dragProvided, dragSnapshot) => (
          <SupportTicketItem
            key={item._id}
            auth={auth}
            isDragging={dragSnapshot.isDragging}
            isGroupedOver={Boolean(dragSnapshot.combineTargetFor)}
            item={item}
            name={name}
            provided={dragProvided}
            onClick={onTicketClick}
          />
        )}
      </Draggable>
    </ItemContainer>
  ));

InnerItemList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  name: PropTypes.string.isRequired,
  removePackageTest: PropTypes.func,
  auth: PropTypes.object.isRequired,
  onTicketClick: PropTypes.func
};

InnerItemList.defaultProps = {
  items: [],
  removePackageTest: () => null,
  onTicketClick: () => null
};

// ///////////////////////
// INNER LIST COMPONENT //
// //////////////////////
const InnerList = ({
  items,
  dropProvided,
  title,
  name,
  removePackageTest,
  auth,
  onTicketClick,
  onLoadMoreClick,
  onLoadMoreEnabled,
  hideLoadMore
}) => (
  <Container>
    {title}
    <DropZone ref={dropProvided.innerRef}>
      <InnerItemList
        auth={auth}
        items={Array.isArray(items) ? items : []}
        name={name}
        removePackageTest={removePackageTest}
        onTicketClick={onTicketClick}
      />
      {dropProvided.placeholder}

      {!hideLoadMore && onLoadMoreClick && items.length ? (
        <input
          className="btn btn-blue"
          onClick={(e) => onLoadMoreClick(e, name)}
          disabled={!onLoadMoreEnabled}
          value="Load More"
          style={{ width: "100%" }}
        />
      ) : null}
    </DropZone>
  </Container>
);
InnerList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  dropProvided: PropTypes.object,
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  removePackageTest: PropTypes.func,
  auth: PropTypes.object.isRequired,
  onTicketClick: PropTypes.func,
  onLoadMoreClick: PropTypes.func,
  onLoadMoreEnabled: PropTypes.bool,
  hideLoadMore: PropTypes.bool
};
InnerList.defaultProps = {
  items: [],
  dropProvided: {},
  title: "",
  removePackageTest: () => null,
  onTicketClick: () => null,
  hideLoadMore: false
};

// ///////////////////////
// ITEM LIST COMPONENT //
// /////////////////////
const ItemList = ({
  ignoreContainerClipping,
  internalScroll,
  scrollContainerStyle,
  isDropDisabled,
  isCombineEnabled,
  listId,
  listType,
  style,
  items,
  title,
  name,
  removePackageTest,
  auth,
  onTicketClick,
  onLoadMoreClick,
  onLoadMoreEnabled,
  hideLoadMore
}) => (
  <Droppable
    droppableId={listId}
    ignoreContainerClipping={ignoreContainerClipping}
    isCombineEnabled={isCombineEnabled}
    isDropDisabled={isDropDisabled}
    type={listType}
  >
    {(dropProvided, dropSnapshot) => (
      <Wrapper
        isDraggingFrom={Boolean(dropSnapshot.draggingFromThisWith)}
        isDraggingOver={dropSnapshot.isDraggingOver}
        isDropDisabled={isDropDisabled}
        style={style}
        {...dropProvided.droppableProps}
      >
        {internalScroll ? (
          <ScrollContainer style={scrollContainerStyle}>
            <InnerList
              auth={auth}
              dropProvided={dropProvided}
              hideLoadMore={hideLoadMore}
              items={items}
              name={name}
              removePackageTest={removePackageTest}
              title={title}
              onTicketClick={onTicketClick}
              onLoadMoreClick={onLoadMoreClick}
              onLoadMoreEnabled={onLoadMoreEnabled}
            />
          </ScrollContainer>
        ) : (
          <InnerList
            auth={auth}
            dropProvided={dropProvided}
            hideLoadMore={hideLoadMore}
            items={items}
            name={name}
            removePackageTest={removePackageTest}
            title={title}
            onTicketClick={onTicketClick}
            onLoadMoreClick={onLoadMoreClick}
            onLoadMoreEnabled={onLoadMoreEnabled}
          />
        )}
      </Wrapper>
    )}
  </Droppable>
);

ItemList.propTypes = {
  ignoreContainerClipping: PropTypes.bool,
  internalScroll: PropTypes.bool,
  scrollContainerStyle: PropTypes.object,
  isDropDisabled: PropTypes.bool,
  isCombineEnabled: PropTypes.bool,
  listId: PropTypes.string,
  listType: PropTypes.string,
  style: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  removePackageTest: PropTypes.func,
  auth: PropTypes.object.isRequired,
  onTicketClick: PropTypes.func,
  onLoadMoreClick: PropTypes.func,
  onLoadMoreEnabled: PropTypes.bool,
  hideLoadMore: PropTypes.bool
};

ItemList.defaultProps = {
  ignoreContainerClipping: false,
  internalScroll: false,
  scrollContainerStyle: {},
  isDropDisabled: false,
  isCombineEnabled: false,
  listId: "",
  listType: "",
  style: {},
  items: [],
  title: "",
  removePackageTest: () => null,
  onTicketClick: () => null,
  hideLoadMore: false
};

// ////////////////////
// COLUMN COMPONENT //
// ///////////////////
const Column = ({
  title,
  items,
  index,
  isScrollable,
  isCombineEnabled,
  name,
  removePackageTest,
  auth,
  onTicketClick,
  onLoadMoreClick,
  onLoadMoreEnabled,
  hideLoadMore
}) => {
  const getColor = (isDragging) => {
    if (isDragging && !auth.isNightMode) {
      return {
        backgroundColor: "#374a5e"
      };
    }
    if (isDragging && auth.isNightMode) {
      return {
        backgroundColor: "#E3FCEF"
      };
    }
    if (!isDragging && auth.isNightMode) {
      return {
        backgroundColor: "#404952"
      };
    }
    return {
      backgroundColor: ""
    };
  };
  return (
    <Draggable id={name} draggableId={title} index={index}>
      {(provided, snapshot) => (
        <Container
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <h3 style={{ textAlign: "center", marginTop: 0 }}>{title}</h3>
          <ItemList
            auth={auth}
            hideLoadMore={hideLoadMore}
            internalScroll={isScrollable}
            isCombineEnabled={Boolean(isCombineEnabled)}
            items={items}
            listId={title}
            listType="ITEM"
            name={name}
            onLoadMoreClick={onLoadMoreClick}
            onLoadMoreEnabled={onLoadMoreEnabled}
            removePackageTest={removePackageTest}
            style={getColor(snapshot.isDragging)}
            onTicketClick={onTicketClick}
          />
        </Container>
      )}
    </Draggable>
  );
};

Column.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  index: PropTypes.number,
  isScrollable: PropTypes.bool,
  isCombineEnabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  removePackageTest: PropTypes.func,
  auth: PropTypes.object.isRequired,
  onTicketClick: PropTypes.func,
  onLoadMoreClick: PropTypes.func,
  onLoadMoreEnabled: PropTypes.bool,
  hideLoadMore: PropTypes.bool
};

Column.defaultProps = {
  title: "",
  items: [],
  index: null,
  isScrollable: false,
  isCombineEnabled: false,
  removePackageTest: () => null,
  onTicketClick: () => null,
  hideLoadMore: false
};

export default Column;
